import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  SquareMousePointer, 
  Globe, 
  GraduationCap, 
  Package, 
  WalletCards, 
  ArrowRight
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useProfile } from '@/hooks/useProfile';
import { useAuthStore } from '@/stores/authStore';

const STORAGE_KEY = 'dashboard-hero-expanded';

interface ActionBoxProps {
  icon?: React.ReactNode;
  title?: string;
  to?: string;
  className?: string;
  wrapperClassName?: string;
  heightClass?: string;
  bgColor?: string;
  textColor?: string;
  layout?: 'icon-top-right-text-bottom-left' | 'icon-bottom-left-cropped-text-top-right' | 'icon-top-right-text-bottom-left-multiline' | 'icon-text-bottom-left' | 'icon-centered-text-below' | 'custom';
  children?: React.ReactNode;
  ariaLabel?: string;
  titleClassName?: string;
}

const ActionBox: React.FC<ActionBoxProps> = ({ 
  icon, 
  title, 
  to, 
  className = '', 
  wrapperClassName = '',
  heightClass = 'h-[140px]',
  bgColor = 'bg-card',
  textColor = 'text-foreground',
  layout = 'icon-top-right-text-bottom-left',
  children,
  ariaLabel,
  titleClassName
}) => {
  const getLayoutClasses = () => {
    switch (layout) {
      case 'icon-top-right-text-bottom-left':
        return 'flex flex-col justify-between h-full';
      case 'icon-bottom-left-cropped-text-top-right':
        return 'relative flex flex-col justify-start h-full';
      case 'icon-top-right-text-bottom-left-multiline':
        return 'flex flex-col justify-between h-full';
      case 'icon-text-bottom-left':
        return 'flex flex-col justify-end h-full';
      case 'icon-centered-text-below':
        return 'flex items-center justify-center h-full';
      case 'custom':
        return 'flex flex-col justify-end h-full';
      default:
        return 'flex flex-col justify-between h-full';
    }
  };

  const content = (
    <Card className={`${heightClass} p-6 hover:shadow-lg transition-all duration-300 overflow-hidden ${bgColor} ${textColor} ${className}`}>
      <div className={getLayoutClasses()}>
        {layout === 'icon-top-right-text-bottom-left' && (
          <>
            <div className="flex justify-end">
              <div className="transition-transform duration-300 group-hover:scale-110">
                {icon}
              </div>
            </div>
            <h3 className={titleClassName || "text-xl font-bold"}>{title}</h3>
          </>
        )}
        
        {layout === 'icon-bottom-left-cropped-text-top-right' && (
          <>
            <div className="flex justify-end text-right">
              <div className="flex flex-col items-end">
                <span className="text-lg font-medium leading-tight">Private Market</span>
                <span className="text-lg font-medium leading-tight">Data</span>
              </div>
            </div>
            <div className="absolute -bottom-10 -left-7">
              <div className="transition-transform duration-300 group-hover:scale-110 origin-bottom-left">
                {icon}
              </div>
            </div>
          </>
        )}
        
        {layout === 'icon-top-right-text-bottom-left-multiline' && (
          <>
            <div className="flex justify-end">
              <div className="transition-transform duration-300 group-hover:scale-110">
                {icon}
              </div>
            </div>
            <div className="flex flex-col items-start text-left max-w-[80%]">
              <span className="text-lg font-medium leading-tight">Portfolio</span>
              <span className="text-lg font-medium leading-tight whitespace-nowrap">Design Academy</span>
            </div>
          </>
        )}
        
        {layout === 'icon-text-bottom-left' && (
          <div className="flex items-center gap-2">
            <div className="transition-transform duration-300 group-hover:scale-110">
              {icon}
            </div>
            <h3 className="text-lg font-semibold whitespace-nowrap">{title}</h3>
          </div>
        )}

        {layout === 'icon-centered-text-below' && (
          <div className="flex flex-col items-center justify-center gap-2 text-center">
            <div className="transition-transform duration-300 group-hover:scale-110">
              {icon}
            </div>
            <h3 className="text-lg font-medium whitespace-nowrap">{title}</h3>
          </div>
        )}

        {layout === 'custom' && children}
      </div>
    </Card>
  );

  if (to) {
    return (
      <Link to={to} aria-label={ariaLabel || title} className={`block group cursor-pointer ${wrapperClassName}`}>
        {content}
      </Link>
    );
  }

  return (
    <div className={`block group cursor-pointer ${wrapperClassName}`}>
      {content}
    </div>
  );
};

export const DashboardHero: React.FC = () => {
  const { user } = useAuthStore();
  const { profile } = useProfile(user);
  const [isExpanded, setIsExpanded] = useState<string | undefined>('hero');

  // Load saved state from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved !== null) {
      setIsExpanded(saved === 'true' ? 'hero' : undefined);
    }
  }, []);

  // Save state to localStorage when it changes
  const handleValueChange = (value: string | undefined) => {
    setIsExpanded(value);
    localStorage.setItem(STORAGE_KEY, value === 'hero' ? 'true' : 'false');
  };

  // Format role for display (investor -> Investor, sys_admin -> Admin, etc.)
  const formatRole = (role?: string) => {
    if (!role) return 'Investor';
    if (role === 'sys_admin') return 'Admin';
    if (role === 'manager_admin') return 'Manager';
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  };
  const displayRole = formatRole((profile as any)?.role);
  // Prefer using the user's first name when available for a friendlier greeting
  const displayName = (profile?.first_name && profile.first_name.trim().length > 0)
    ? profile.first_name.trim()
    : displayRole;

  return (
    <Accordion 
      type="single" 
      collapsible 
      value={isExpanded}
      onValueChange={handleValueChange}
      className="w-full"
    >
      <AccordionItem value="hero" className="border-b-0">
        <AccordionTrigger className="px-6 py-4 hover:no-underline">
          <div className="flex-1 flex justify-end">
            <h2 className="text-lg font-semibold">Welcome {displayName}</h2>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-6 pb-6">
          <div className="space-y-4 mt-2">
            {/* Row 1: 33/33/33 split */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ActionBox
                icon={<SquareMousePointer className="h-8 w-8 scale-x-[-1]" strokeWidth={1.5} />}
                title="Get Started"
                bgColor="bg-brand-hero"
                textColor="text-brand-hero-foreground"
                layout="icon-top-right-text-bottom-left"
                heightClass="h-[120px]"
                titleClassName="text-xl font-semibold"
              />
              <ActionBox
                icon={<Globe className="h-[4.5rem] w-[4.5rem] rotate-[45deg]" strokeWidth={1} />}
                to="/explore"
                bgColor="bg-card"
                textColor="text-foreground"
                layout="icon-bottom-left-cropped-text-top-right"
                ariaLabel="Private Market Data"
                heightClass="h-[120px]"
              />
              <ActionBox
                icon={<GraduationCap className="h-8 w-8" strokeWidth={1.5} />}
                bgColor="bg-foreground"
                textColor="text-background"
                layout="icon-top-right-text-bottom-left-multiline"
                heightClass="h-[120px]"
              />
            </div>

            {/* Row 2: 20/20/40 split with 20% whitespace */}
            <div className="grid grid-cols-1 md:grid-cols-10 gap-4">
              <ActionBox
                icon={<Package className="h-8 w-8" strokeWidth={1.5} />}
                title="+ Assets"
                to="/assets"
                bgColor="bg-card"
                textColor="text-foreground"
                layout="icon-centered-text-below"
                wrapperClassName="md:col-span-2"
                ariaLabel="Add Assets"
              />
              <ActionBox
                icon={<WalletCards className="h-8 w-8" strokeWidth={1.5} />}
                title="+ Portfolio"
                to="/portfolios"
                bgColor="bg-card"
                textColor="text-foreground"
                layout="icon-centered-text-below"
                wrapperClassName="md:col-span-2"
                ariaLabel="Add Portfolio"
              />
              <ActionBox
                to="/investments/connect"
                bgColor="bg-[hsl(var(--muted-strong))]"
                textColor="text-foreground"
                layout="custom"
                wrapperClassName="md:col-span-4"
                ariaLabel="Connect Investment Accounts"
              >
                <div className="space-y-0">
                  <p className="text-lg font-semibold leading-tight">Connect Investment</p>
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-semibold leading-tight">Accounts</p>
                    <ArrowRight className="h-8 w-8 transition-transform duration-300 group-hover:translate-x-2" strokeWidth={2} />
                  </div>
                </div>
              </ActionBox>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
